// src/components/black-friday/insights-view.tsx
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BFDashboardMetrics } from "@/types/black-friday";
import { TrendingUp, Search, Filter, Ban } from "lucide-react";

interface Props {
  dashboard?: BFDashboardMetrics;
}

function formatCurrency(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

export default function InsightsView({ dashboard }: Props) {
  if (!dashboard) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <div className="text-center space-y-4">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-2xl font-semibold">No Insights Data Available</h3>
            <p className="text-muted-foreground max-w-md">
              Run the Black Friday script in your Google Ads account to see detailed insights.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Highest CPC Keywords */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <CardTitle>Highest CPC Keywords & Search Terms</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Highest CPC Keyword</p>
              <div className="flex items-baseline gap-3">
                <p className="text-2xl font-bold text-red-600">{formatCurrency(dashboard.highestCpcKeywordCpc)}</p>
                <p className="text-lg font-medium">{dashboard.highestCpcKeyword || "—"}</p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Highest CPC Search Term</p>
              <div className="flex items-baseline gap-3">
                <p className="text-2xl font-bold text-red-600">{formatCurrency(dashboard.highestCpcSearchTermCpc)}</p>
                <p className="text-lg font-medium">{dashboard.highestCpcSearchTerm || "—"}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Match Type Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-purple-500" />
            <CardTitle>Match Type Performance</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Exact Match ({dashboard.exactConv.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} conversions)
              </p>
              <p className="text-xl font-bold">{formatCurrency(dashboard.exactCost)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Phrase Match ({dashboard.phraseConv.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} conversions)
              </p>
              <p className="text-xl font-bold">{formatCurrency(dashboard.phraseCost)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Broad Match ({dashboard.broadConv.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} conversions)
              </p>
              <p className="text-xl font-bold">{formatCurrency(dashboard.broadCost)}</p>
            </div>
            <div className="space-y-1">
              {/* Empty column for layout consistency */}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* N-gram Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-emerald-500" />
            <CardTitle>Search Term N-gram Analysis</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Total Search Terms</p>
              <p className="text-2xl font-bold">{formatNumber(dashboard.ngramSearchTerms)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Unique Words</p>
              <p className="text-2xl font-bold">{formatNumber(dashboard.ngramUniqueWords)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">New Words (not in keywords)</p>
              <p className="text-2xl font-bold">{formatNumber(dashboard.ngramNewWords)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">New Words Cost</p>
              <p className="text-2xl font-bold">{formatCurrency(dashboard.ngramNewWordsCost)}</p>
              <p className="text-xs text-muted-foreground">Total cost of search terms containing new words</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Negatives Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-amber-500" />
            <CardTitle>Negative Keywords Summary</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Campaign Negatives</p>
              <p className="text-2xl font-bold">{formatNumber(dashboard.campaignNegatives)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Ad Group Negatives</p>
              <p className="text-2xl font-bold">{formatNumber(dashboard.adGroupNegatives)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Negative Lists</p>
              <p className="text-2xl font-bold">{formatNumber(dashboard.negativeLists)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Total in Lists</p>
              <p className="text-2xl font-bold">{formatNumber(dashboard.totalNegativesInLists)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
